import { useState, useEffect, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import './SharepointLink.scss';
import { GetSharepointFile } from '../../../api/transfer';
import { useMutation } from '@tanstack/react-query';
import { Snackbar } from '../Snackbar/Snackbar';
import { fetchSecuredFile, fetchSignedURL } from '../../../api/file';
import { useLinkTypes } from '../../../hooks/useLinkTypes';

function SharepointLink(
  { link, fileName, children, className, id, style, linkType, onMouseEnter, onMouseLeave, baseUrl },
  ref
) {
  const [filePath, setFilePath] = useState();
  const [fileIsFetching, setFileIsFetching] = useState(false);
  const [fileFetchIsError, setFileFetchIsError] = useState(false);
  const linkRef = useRef();
  const linkTypeQuery = useLinkTypes();
  const newLink = linkType === linkTypeQuery.LINKTYPE.FILE ? `${baseUrl}/${link}` : link;

  const getSharepointFileQuery = useMutation({
    mutationFn: async () => await GetSharepointFile({ Url: link, FileName: fileName })(),
  });

  // Handle success logic with useEffect
  useEffect(() => {
    if (getSharepointFileQuery.isSuccess && getSharepointFileQuery.data) {
      setFilePath(getSharepointFileQuery.data);
    }
  }, [getSharepointFileQuery.isSuccess, getSharepointFileQuery.data]);

  const handleSecuredFile = async (path) => {
    try {
      setFileIsFetching(true);
      await fetchSignedURL(path);
    } catch (error) {
      setFileFetchIsError(true);
      setFileIsFetching(false);
    } finally {
      setFileIsFetching(false);
    }
  };

  useEffect(() => {
    if (filePath) {
      linkRef.current.click();
    }
  }, [filePath]);

  if (linkTypeQuery.isPending) return <CircularProgress size="1em" />;

  if (newLink == null || newLink === undefined) return <></>;

  if (linkType === linkTypeQuery.LINKTYPE.NOTHING) {
    return (
      <span ref={ref} id={id || ''} className={className} style={style}>
        {children}
      </span>
    );
  }

  // Check: is link from agentsite CMS?
  if (newLink.includes('baltlife.sharepoint.com')) {
    if (filePath) {
      return (
        <a
          ref={linkRef}
          id={id || ''}
          className={className || ''}
          style={style}
          href={filePath}
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      );
    }
    if (getSharepointFileQuery.isPending) {
      return (
        <a
          ref={ref}
          id={id || ''}
          className={className ? `${className} ` : '' + 'loading'}
          style={style}
          disabled="disabled"
        >
          {children}
          <CircularProgress size={16} />
        </a>
      );
    }
    return (
      <a
        id={id || ''}
        ref={ref}
        className={className || ''}
        style={style}
        onClick={() => {
          getSharepointFileQuery.mutate();
        }}
      >
        {children}
      </a>
    );
  }
  // default case: return an 'a' element with an href of props.link
  return (
    <>
      <Snackbar
        open={fileFetchIsError}
        message={"Couldn't retrieve file"}
        status="error"
        duration={3000}
        onClose={() => setFileFetchIsError(false)}
      />
      {linkType === linkTypeQuery.LINKTYPE.NOTHING ? (
        <span ref={ref} id={id || ''} className={className} style={style}>
          {children}
        </span>
      ) : linkType === linkTypeQuery.LINKTYPE.FILE ? (
        <>
          <a
            ref={ref}
            id={id || ''}
            className={className}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={(e) => {
              e.preventDefault();
              handleSecuredFile(newLink);
            }}
            style={{ ...style, display: 'flex', alignItems: 'center' }}
          >
            {children}{' '}
            {fileIsFetching && <CircularProgress size={16} style={{ marginLeft: '.5rem' }} />}
          </a>
        </>
      ) : (
        <a
          ref={ref}
          href={newLink}
          id={id || ''}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={className}
          target="_blank"
          style={style}
          rel="noopener noreferrer"
        >
          {children}
        </a>
      )}
    </>
  );
}

const SharepointLinkWithRef = forwardRef(SharepointLink);

SharepointLinkWithRef.propTypes = {
  link: PropTypes.string.isRequired,
};

export default SharepointLinkWithRef;
